import { useEffect, useRef } from 'react'

export const canvasDimensions = { height: 1080, width: 1920 }
// export const canvasDimensions = { height: 1280, width: 720 }
// export const canvasDimensions = { height: 720, width: 480 }

export const useInitCameraStream = (front = false) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const mobileOS = getMobileOS()
  const aspectRatio = mobileOS === 'Android' ? 9 / 16 : undefined

  const initCameraStream = async () => {
    const mediaDevices = navigator.mediaDevices
    const videoPreview = videoRef.current
    if (!mediaDevices) return
    if (!videoPreview) return

    const constraints: MediaStreamConstraints = {
      video: {
        width: { ideal: canvasDimensions.width },
        height: { ideal: canvasDimensions.height },
        facingMode: front ? 'user' : 'environment',
        aspectRatio,
      },
    }

    if (navigator.mediaDevices.enumerateDevices) {
      const deviceId = await new Promise<string>((resolve, reject) => {
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            for (const device of devices) {
              if (device.kind === 'videoinput') {
                if (device.label.includes(front ? 'front' : 'back')) {
                  resolve(device.deviceId)
                } else {
                  resolve('')
                }
              }
            }
          })
          .catch((err) => {
            console.error(`${err.name}: ${err.message}`)
            reject('no devices')
          })
      }).catch(console.log)

      if (deviceId) {
        ;(constraints.video as MediaTrackConstraints).deviceId = deviceId
      }
    }

    const stream = await mediaDevices.getUserMedia(constraints)

    videoPreview.srcObject = stream
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: initCameraStream は front 以外の描画値に依存しないため、カメラ向きの切り替え時のみストリームを再初期化する
  useEffect(() => {
    initCameraStream()

    const video = videoRef.current

    return () => {
      if (video?.srcObject) {
        const stream = video.srcObject as MediaStream

        for (const track of stream.getTracks()) {
          track.stop()
        }
      }
    }
  }, [front])

  return videoRef
}

export const getCameraStream = async (front = false) => {
  const mobileOS = getMobileOS()
  const aspectRatio = mobileOS === 'Android' ? 9 / 16 : undefined

  const mediaDevices = navigator.mediaDevices
  if (!mediaDevices) return

  const constraints = {
    width: { ideal: canvasDimensions.width },
    height: { ideal: canvasDimensions.height },
    facingMode: front ? 'user' : 'environment',
    aspectRatio,
  }

  return await mediaDevices.getUserMedia({
    video: constraints,
  })
}

export const preloadImages = async (frames: string[]) => {
  try {
    const loadImage = (imageUrl: string) =>
      new Promise<HTMLImageElement | null>((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'Anonymous'
        image.src = `${imageUrl}?${Date.now()}`
        image.onload = () => resolve(image)
        image.onerror = () => resolve(null)
      })

    const promiseList = frames.map(loadImage)

    const resolvedImages = await Promise.all(promiseList)

    return resolvedImages.filter((x) => !!x)
  } catch (error) {
    return []
  }
}

export const isGalaxyDevice = () => {
  if (typeof navigator === 'undefined') {
    return false
  }

  console.log(navigator.userAgent)
  const mobileOS = getMobileOS()
  const isGalaxy = /samsung|galaxy|sm-/i.test(navigator.userAgent)
  return mobileOS === 'Android' && isGalaxy
}

export function getSupportedMineTypeWithCodec() {
  // Safari対応: codecではなくcodecsを使用、iOS/Safari用のコーデックを優先
  const mobileOS = getMobileOS()

  let codecList: string[] = []

  if (mobileOS === 'iOS') {
    // iOS/Safari用のコーデック優先順位
    codecList = [
      'video/mp4;codecs=h264', // Safariで最も互換性が高い
      'video/mp4;codecs=avc1.42E01E', // Safari用のH.264プロファイル
      'video/mp4', // フォールバック
      'video/webm;codecs=vp8', // 最後の手段
    ]
  } else {
    if (isGalaxyDevice()) {
      // Android/その他のブラウザ用
      codecList = [
        'video/mp4;codecs=avc1.42E01E',
        // vp9が一番のおすすめだが、カクカクしているため、avc1.42E01Eを優先している
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/mp4;codecs=h264',
        'video/mp4',
      ]
    } else {
      // Android/その他のブラウザ用
      codecList = [
        // vp9が一番のおすすめだが、カクカクしているため、avc1.42E01Eを優先している
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/mp4;codecs=h264',
        'video/mp4',
      ]
    }
  }

  //サポートを確認
  let supportedCodec = ''

  codecList.every((codec) => {
    if (MediaRecorder.isTypeSupported(codec)) {
      supportedCodec = codec
      return false
    }
    return true
  })
  for (const codec of codecList) {
    console.log(`${codec} is supported: ${MediaRecorder.isTypeSupported(codec)}`)
  }

  return supportedCodec
}

export const getMobileOS = () => {
  if (typeof navigator === 'undefined') {
    return ''
  }

  const userAgent = navigator.userAgent
  if (/android/i.test(userAgent)) {
    return 'Android'
  }
  if (/iPad|iPhone|iPod/.test(userAgent) || navigator.maxTouchPoints > 1) {
    return 'iOS'
  }

  return 'Other'
}

export const getOptimalFrameRate = () => {
  const mobileOS = getMobileOS()

  // その他のAndroidデバイスでは30fps
  if (mobileOS === 'Android') {
    return 30
  }

  // iOSでは30fps
  return 30
}
